import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from 'react-i18next';
import { useQueryClient } from "@tanstack/react-query";
import { Bell, Check, Loader2, XCircle, Trash2 } from "lucide-react";
import {
  getNotifications,
  getUnreadNotificationCount,
  markNotificationAsRead,
  deleteNotification,
  acceptInvite,
  declineInvite,
  type Notification,
  type SharePermission,
} from "../services/sharing.service";
import { timeAgo } from "../lib/dates";
import { toast } from "../lib/toast";

// allow: SIZE_OK — One lifecycle state machine coordinates requests, mutations, and rendered controls.
interface NotificationBellProps {
  readonly refreshInterval?: number;
  readonly open?: boolean;
  readonly onOpenChange?: (open: boolean) => void;
}

type ActiveRequest = {
  readonly controller: AbortController;
  readonly promise: Promise<void>;
};

const MIN_REFRESH_INTERVAL_MS = 60_000;

export function NotificationBell({
  refreshInterval = MIN_REFRESH_INTERVAL_MS,
  open: controlledOpen,
  onOpenChange
}: NotificationBellProps) {
  const { t } = useTranslation('common');
  const queryClient = useQueryClient();
  const [internalOpen, setInternalOpen] = useState(false);
  const open = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [actionLoadingId, setActionLoadingId] = useState<number | null>(null);
  const mountedRef = useRef(false);
  const mutationQueueRef = useRef<Promise<void>>(Promise.resolve());
  const countRequestRef = useRef<ActiveRequest | null>(null);
  const listRequestRef = useRef<ActiveRequest | null>(null);
  const unreadRevisionRef = useRef(0);
  const listRevisionRef = useRef(0);
  const lifecycleRevisionRef = useRef(0);
  const visibilityRef = useRef(document.visibilityState === "visible");
  const pollInterval = Math.max(refreshInterval, MIN_REFRESH_INTERVAL_MS);

  const loadUnreadCount = useCallback((replace = false): Promise<void> => {
    if (!mountedRef.current) {
      return Promise.resolve();
    }
    const activeRequest = countRequestRef.current;
    if (activeRequest && !replace) {
      return activeRequest.promise;
    }
    activeRequest?.controller.abort();

    const controller = new AbortController();
    const revision = ++unreadRevisionRef.current;
    const promise = getUnreadNotificationCount(controller.signal)
      .then((count) => {
        if (mountedRef.current && revision === unreadRevisionRef.current) {
          setUnreadCount(count);
        }
      })
      .catch((error: unknown) => {
        if (!controller.signal.aborted) {
          console.error(error);
        }
      })
      .finally(() => {
        if (countRequestRef.current?.controller === controller) {
          countRequestRef.current = null;
        }
      });
    countRequestRef.current = { controller, promise };
    return promise;
  }, []);

  const loadNotifications = useCallback((replace = false): Promise<void> => {
    if (!mountedRef.current) {
      return Promise.resolve();
    }
    const activeRequest = listRequestRef.current;
    if (activeRequest && !replace) {
      return activeRequest.promise;
    }
    activeRequest?.controller.abort();

    const controller = new AbortController();
    const listRevision = ++listRevisionRef.current;
    const unreadRevision = ++unreadRevisionRef.current;
    setLoading(true);
    const promise = getNotifications(controller.signal)
      .then((response) => {
        if (!mountedRef.current) {
          return;
        }
        if (listRevision === listRevisionRef.current) {
          setItems(response.items);
        }
        if (unreadRevision === unreadRevisionRef.current) {
          setUnreadCount(response.unreadCount);
        }
      })
      .catch((error: unknown) => {
        if (!controller.signal.aborted) {
          console.error(error);
        }
      })
      .finally(() => {
        if (listRequestRef.current?.controller === controller) {
          listRequestRef.current = null;
          if (mountedRef.current) {
            setLoading(false);
          }
        }
      });
    listRequestRef.current = { controller, promise };
    return promise;
  }, []);

  const invalidatePendingResponses = useCallback(() => {
    unreadRevisionRef.current += 1;
    listRevisionRef.current += 1;
    countRequestRef.current?.controller.abort();
    listRequestRef.current?.controller.abort();
  }, []);

  const refreshAfterMutation = useCallback(async (): Promise<void> => {
    await Promise.all([
      loadNotifications(true),
      loadUnreadCount(true),
    ]);
  }, [loadNotifications, loadUnreadCount]);

  const enqueueMutation = useCallback(
    (mutation: () => Promise<void>): Promise<void> => {
      const execute = async (): Promise<void> => {
        if (!mountedRef.current) {
          return;
        }
        invalidatePendingResponses();
        await mutation();
        await refreshAfterMutation();
      };
      const queuedMutation = mutationQueueRef.current.then(execute, execute);
      mutationQueueRef.current = queuedMutation;
      return queuedMutation;
    },
    [invalidatePendingResponses, refreshAfterMutation]
  );

  const disposeRequestsIfUnmounted = useCallback((revision: number) => {
    if (mountedRef.current || revision !== lifecycleRevisionRef.current) {
      return;
    }
    unreadRevisionRef.current += 1;
    listRevisionRef.current += 1;
    const countRequest = countRequestRef.current;
    const listRequest = listRequestRef.current;
    countRequestRef.current = null;
    listRequestRef.current = null;
    countRequest?.controller.abort();
    listRequest?.controller.abort();
  }, []);

  useEffect(() => {
    const lifecycleRevision = ++lifecycleRevisionRef.current;
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      queueMicrotask(() => {
        disposeRequestsIfUnmounted(lifecycleRevision);
      });
    };
  }, [disposeRequestsIfUnmounted]);

  useEffect(() => {
    let timer: ReturnType<typeof setInterval> | null = null;
    const stopPolling = () => {
      if (timer !== null) {
        clearInterval(timer);
        timer = null;
      }
    };
    const startPolling = () => {
      if (timer === null) {
        timer = setInterval(() => {
          if (document.visibilityState === "visible" && !open) {
            void loadUnreadCount();
          }
        }, pollInterval);
      }
    };
    const handleVisibilityChange = () => {
      const isVisible = document.visibilityState === "visible";
      if (isVisible === visibilityRef.current) {
        return;
      }
      visibilityRef.current = isVisible;
      stopPolling();
      if (!isVisible) {
        unreadRevisionRef.current += 1;
        const countRequest = countRequestRef.current;
        countRequestRef.current = null;
        countRequest?.controller.abort();
        return;
      }
      if (!open) {
        void loadUnreadCount();
        startPolling();
      }
    };

    visibilityRef.current = document.visibilityState === "visible";
    if (visibilityRef.current && !open) {
      void loadUnreadCount();
      startPolling();
    }
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      stopPolling();
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [loadUnreadCount, open, pollInterval]);

  useEffect(() => {
    if (open) {
      void loadNotifications();
    }
  }, [loadNotifications, open]);

  const toggle = () => {
    const newOpen = !open;
    if (onOpenChange) {
      onOpenChange(newOpen);
    } else {
      setInternalOpen(newOpen);
    }
  };

  const markAsRead = async (id: number) => {
    try {
      await enqueueMutation(async () => {
        await markNotificationAsRead(id);
      });
    } catch (error: unknown) {
      toast.error(
        error instanceof Error && error.message
          ? error.message
          : t('notifications.error_processing')
      );
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await enqueueMutation(async () => {
        await deleteNotification(id);
      });
    } catch (error: unknown) {
      toast.error(
        error instanceof Error && error.message
          ? error.message
          : t('notifications.error_deleting')
      );
    }
  };

  const permissionLabels: Record<SharePermission, string> = {
    view: t('notifications.permission_view'),
    edit: t('notifications.permission_edit'),
    owner: t('notifications.permission_owner'),
  };

  const handleInvitationAction = async (
    notification: Notification,
    action: "accept" | "decline"
  ) => {
    const metadata = notification.metadata ?? {};
    const token = typeof metadata.token === "string" ? metadata.token : undefined;

    if (!token) {
      toast.error(t('notifications.invalid_invite'));
      return;
    }

    try {
      setActionLoadingId(notification.id);
      await enqueueMutation(async () => {
        if (action === "accept") {
          await acceptInvite(token);
          toast.success(t('notifications.invite_accepted'));

          if (notification.resource_type === 'project') {
            queryClient.invalidateQueries({ queryKey: ['projects'] });
            window.dispatchEvent(new CustomEvent('shared-projects-updated'));
          }
        } else {
          await declineInvite(token);
          toast.success(t('notifications.invite_declined'));
          await markNotificationAsRead(notification.id);
        }
      });
    } catch (error: unknown) {
      toast.error(
        error instanceof Error && error.message
          ? error.message
          : t('notifications.error_processing')
      );
    } finally {
      if (mountedRef.current) {
        setActionLoadingId(null);
      }
    }
  };

  const displayUnread = useMemo(() => (unreadCount > 9 ? "9+" : String(unreadCount)), [unreadCount]);

  return (
    <div className="relative">
      <button
        onClick={toggle}
        className="relative flex items-center justify-center w-10 h-10 rounded-full hover:bg-muted transition-colors"
        aria-label={t('notifications.title')}
      >
        <Bell size={18} className="text-muted-foreground" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-[#013EFB] text-white text-[10px] font-semibold rounded-full px-1.5 py-0.5">
            {displayUnread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-80 bg-popover rounded-lg shadow-lg border border-border z-50">
          <div className="px-4 py-3 border-b border-border flex justify-between items-center">
            <h3 className="text-sm font-semibold text-foreground">{t('notifications.title')}</h3>
            {loading && <span className="text-xs text-muted-foreground">{t('notifications.updating')}</span>}
          </div>
          <div className="max-h-80 overflow-y-auto">
            {items.length === 0 && (
              <div className="px-4 py-6 text-center text-sm text-muted-foreground">
                {t('notifications.no_notifications')}
              </div>
            )}
            {items.map((notification) => {
              const metadata = notification.metadata ?? {};
              const permissionValue = metadata.permission;
              const permissionLabel =
                permissionValue === "view" ||
                permissionValue === "edit" ||
                permissionValue === "owner"
                  ? permissionLabels[permissionValue]
                  : undefined;

              // Formatar título de forma amigável
              const getNotificationTitle = () => {
                if (notification.title) return notification.title;
                switch (notification.type) {
                  case "invitation": return "🎉 Novo convite!";
                  case "access_granted": return "✅ Acesso concedido";
                  case "access_revoked": return "❌ Acesso alterado";
                  case "permission_changed": return "🔄 Permissão atualizada";
                  default: return String(notification.type).replace("_", " ");
                }
              };

              return (
                <div
                  key={notification.id}
                  className={`px-4 py-3 border-b border-border text-sm ${
                    notification.read_at ? "bg-popover" : "bg-blue-500/10"
                  }`}
                >
                  <div className="flex justify-between items-start gap-3">
                    <div className="flex-1">
                      <p className="font-medium text-foreground">
                        {getNotificationTitle()}
                      </p>
                      {notification.message && (
                        <p className="text-muted-foreground text-xs mt-1">{notification.message}</p>
                      )}
                      {permissionLabel && (
                        <p className="text-muted-foreground text-xs mt-1">{t('notifications.permission')}: {permissionLabel}</p>
                      )}
                      <p className="text-muted-foreground/60 text-xs mt-1">
                        {timeAgo(notification.created_at)}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {!notification.read_at && notification.type !== "invitation" && (
                        <button
                          onClick={() => markAsRead(notification.id)}
                          className="text-xs text-primary hover:text-primary/80 flex items-center gap-1"
                          title={t('notifications.mark_as_read')}
                        >
                          <Check size={12} />
                        </button>
                      )}
                      <button
                        onClick={() => handleDelete(notification.id)}
                        className="text-xs text-muted-foreground hover:text-destructive transition-colors"
                        title={t('notifications.delete')}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                  {notification.type === "invitation" && (
                  <div className="flex gap-2 mt-3">
                    <button
                      onClick={() => handleInvitationAction(notification, "accept")}
                      disabled={actionLoadingId === notification.id}
                      className="flex-1 inline-flex items-center justify-center gap-1 rounded-md bg-primary text-primary-foreground py-1.5 text-xs font-medium hover:bg-primary/90 transition-colors disabled:opacity-60"
                    >
                      {actionLoadingId === notification.id ? (
                        <Loader2 size={14} className="animate-spin" />
                      ) : (
                        <Check size={14} />
                      )}
                      {t('notifications.accept')}
                    </button>
                    <button
                      onClick={() => handleInvitationAction(notification, "decline")}
                      disabled={actionLoadingId === notification.id}
                      className="flex-1 inline-flex items-center justify-center gap-1 rounded-md border border-border py-1.5 text-xs font-medium text-foreground hover:bg-muted transition-colors disabled:opacity-60"
                    >
                      {actionLoadingId === notification.id ? (
                        <Loader2 size={14} className="animate-spin" />
                      ) : (
                        <XCircle size={14} />
                      )}
                      {t('notifications.decline')}
                    </button>
                  </div>
                  )}
                  {!notification.read_at && notification.type === "invitation" && (
                    <button
                      onClick={() => markAsRead(notification.id)}
                      className="mt-2 text-[11px] text-gray-500 hover:text-gray-700"
                    >
                      {t('notifications.mark_as_read')}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
